/**
 * capacityApi.ts
 * Feature: 116 - Entity Detail Modals
 * Author: Peter Dobler
 * Date: 2026-01-29
 * Constitution Version: 1.12.0
 *
 * Purpose: Frontend API service for capacity entities.
 */

import { apiClient } from './apiClient';

/** Capacity Type per Constitution v1.3.0 */
export enum CapacityType {
  Internal = 1,
  Client = 2,
  Trial = 3,
  Development = 4,
}

/** Capacity entity */
export interface Capacity {
  id: string;
  fabric_capacity_id: string;
  capacity_name: string;
  display_name?: string;
  capacity_type: CapacityType;
  criticality: 'critical' | 'high' | 'medium' | 'low';
  subscription_id: string;
  resource_group?: string;
  region?: string;
  sku_name?: string;
  sku_tier?: string;
  state?: string;
  client_account_id?: string;
  environment_id?: string;
  // Control Plane metadata
  owner_id?: string;
  maintenance_window?: string;
  auto_pause_settings?: string;
  sla_tier?: string;
  cost_center?: string;
  notes?: string;
  // Computed fields
  owner_name?: string;
  client_name?: string;
  environment_name?: string;
  workspace_count?: number;
  lakehouse_count?: number;
  created_at: string;
  updated_at: string;
}

/** Capacity update request */
export interface UpdateCapacityRequest {
  display_name?: string;
  owner_id?: string;
  maintenance_window?: string;
  auto_pause_settings?: string;
  sla_tier?: string;
  cost_center?: string;
  notes?: string;
}

/** Capacity filters */
export interface CapacityFilters {
  capacity_type?: CapacityType;
  client_account_id?: string;
  environment_id?: string;
  criticality?: string;
  region?: string;
  state?: string;
}

/**
 * Get capacity by ID
 */
export async function getCapacityById(id: string): Promise<Capacity> {
  const response = await apiClient.get<{ data: Capacity }>(`/api/capacities/${id}`);
  return response.data.data;
}

/**
 * Get all capacities with optional filters
 */
export async function getCapacities(filters?: CapacityFilters): Promise<Capacity[]> {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, String(value));
      }
    });
  }
  const queryString = params.toString();
  const url = queryString ? `/api/capacities?${queryString}` : '/api/capacities';
  const response = await apiClient.get<{ data: Capacity[] }>(url);
  return response.data.data;
}

/**
 * Update capacity (Control Plane metadata only)
 */
export async function updateCapacity(
  id: string,
  data: UpdateCapacityRequest
): Promise<Capacity> {
  const response = await apiClient.patch<{ data: Capacity }>(`/api/capacities/${id}`, data);
  return response.data.data;
}

// TanStack Query hooks

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

/** Query key for capacities */
export const CAPACITY_QUERY_KEYS = {
  all: ['capacities'] as const,
  lists: () => [...CAPACITY_QUERY_KEYS.all, 'list'] as const,
  list: (filters?: CapacityFilters) => [...CAPACITY_QUERY_KEYS.lists(), filters] as const,
  details: () => [...CAPACITY_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...CAPACITY_QUERY_KEYS.details(), id] as const,
};

/**
 * Hook to fetch capacity by ID
 */
export function useCapacity(
  id: string | null,
  options?: Omit<UseQueryOptions<Capacity>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: CAPACITY_QUERY_KEYS.detail(id ?? ''),
    queryFn: () => getCapacityById(id!),
    enabled: !!id,
    ...options,
  });
}

/**
 * Hook to fetch all capacities
 */
export function useCapacities(
  filters?: CapacityFilters,
  options?: Omit<UseQueryOptions<Capacity[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: CAPACITY_QUERY_KEYS.list(filters),
    queryFn: () => getCapacities(filters),
    ...options,
  });
}

/**
 * Hook to update capacity
 */
export function useUpdateCapacity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCapacityRequest }) =>
      updateCapacity(id, data),
    onSuccess: (updatedCapacity) => {
      // Invalidate and update cache
      queryClient.invalidateQueries({ queryKey: CAPACITY_QUERY_KEYS.all });
      queryClient.setQueryData(
        CAPACITY_QUERY_KEYS.detail(updatedCapacity.id),
        updatedCapacity
      );
    },
  });
}

/**
 * Get capacity type display label
 */
export function getCapacityTypeLabel(type: CapacityType): string {
  switch (type) {
    case CapacityType.Internal:
      return 'Internal';
    case CapacityType.Client:
      return 'Client';
    case CapacityType.Trial:
      return 'Trial';
    case CapacityType.Development:
      return 'Development';
    default:
      return 'Unknown';
  }
}

export default {
  getCapacityById,
  getCapacities,
  updateCapacity,
  getCapacityTypeLabel,
};
